import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { featureFlagsService } from '@/services/feature-flags.service';
import { CreateFeatureFlagDto, UpdateFeatureFlagDto, FeatureFlagFilters } from '@/types/feature-flags';

export function useFeatureFlags(productId: string, filters?: FeatureFlagFilters) {
  return useQuery({
    queryKey: ['feature-flags', productId, filters],
    queryFn: () => featureFlagsService.getFeatureFlags(productId, filters),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useFeatureFlag(productId: string, flagId: string) {
  return useQuery({
    queryKey: ['feature-flags', productId, flagId],
    queryFn: () => featureFlagsService.getFeatureFlag(productId, flagId),
    enabled: !!productId && !!flagId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateFeatureFlag() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: CreateFeatureFlagDto }) =>
      featureFlagsService.createFeatureFlag(productId, data),
    onSuccess: (newFlag, { productId }) => {
      // Invalidate and refetch all feature flags queries for this product
      queryClient.invalidateQueries({ 
        queryKey: ['feature-flags', productId],
        exact: false 
      });
      
      // Also set the individual flag cache
      queryClient.setQueryData(['feature-flags', productId, newFlag.id], newFlag);
    },
  });
}

export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ productId, flagId, data }: { productId: string; flagId: string; data: UpdateFeatureFlagDto }) =>
      featureFlagsService.updateFeatureFlag(productId, flagId, data),
    onSuccess: (updatedFlag, { productId, flagId }) => {
      // Update individual flag cache
      queryClient.setQueryData(['feature-flags', productId, flagId], updatedFlag);
      
      // Invalidate list queries to ensure consistency
      queryClient.invalidateQueries({ 
        queryKey: ['feature-flags', productId],
        exact: false 
      });
    },
  });
}

export function useDeleteFeatureFlag() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ productId, flagId }: { productId: string; flagId: string }) =>
      featureFlagsService.deleteFeatureFlag(productId, flagId),
    onSuccess: (_, { productId, flagId }) => {
      // Remove from individual flag cache
      queryClient.removeQueries({ queryKey: ['feature-flags', productId, flagId] });
      
      // Invalidate list queries
      queryClient.invalidateQueries({ 
        queryKey: ['feature-flags', productId],
        exact: false 
      });
    },
  });
}

export function usePlanFeatureFlags(planId: string) {
  return useQuery({
    queryKey: ['plan-feature-flags', planId],
    queryFn: () => featureFlagsService.getPlanFeatureFlags(planId),
    enabled: !!planId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdatePlanFeatureFlags() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ planId, featureFlags }: { planId: string; featureFlags: { [key: string]: boolean } }) =>
      featureFlagsService.updatePlanFeatureFlags(planId, featureFlags),
    onSuccess: (_, { planId }) => {
      queryClient.invalidateQueries({ queryKey: ['plan-feature-flags', planId] });
    },
  });
}

export function useCustomerFeatureFlags(customerId: string) {
  return useQuery({
    queryKey: ['customer-feature-flags', customerId],
    queryFn: () => featureFlagsService.getCustomerFeatureFlags(customerId),
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useFeatureFlagRoleOptions(productId: string) {
  return useQuery({
    queryKey: ['feature-flag-role-options', productId],
    queryFn: () => featureFlagsService.getRoleOptions(productId),
    enabled: !!productId,
    staleTime: 10 * 60 * 1000,
  });
}
